import * as THREE from 'three';
import { Visualizer } from '../visualizer.js';
import { safeZoneUniforms } from '../shaders/safeZone.glsl.js';
import { vdjColor, vdjBeatChroma, VdjPost, VdjComfort, VDJ_DROP_FLASH } from './lib/vdjLook.js';
import { SceneClock } from './lib/scene-clock.js';
import { Adapt } from './lib/adapt.js';

// Contrat du rig partagé : interdit. La lumière de la scène, ce sont les éclats.
const RIG = 'forbidden';
export { RIG };

export const PROFILE = {
  id: 'pyromusical',
  family: 'stage',
  danceability: 0.84,
  requiresTier: 0,
  energyLevel: 0.78,
  bpmMin: 80,
  bpmMax: 170,
  movement: 0.75,
  tonality: 0.65,
  enabled: true
};

// PYROMUSICAL : un feu d'artifice de concours au-dessus d'une baie, la nuit.
// Chaque bombe est tirée À L'AVANCE pour éclater PILE sur un temps.
// Preset party de vdjLook ; pop, EDM, house, variété, rock.
//
// LA SYNCHRONISATION. Le temps de la musique est connu d'avance : la
// coordonnée de temps continue B = temps comptés + beatPhase avance de 1 par
// période. Chaque croche future X (entier = temps, demi = croche) tombe à
// t = maintenant + (X - B) x période. On PLANIFIE chaque croche dès qu'elle
// entre à 2,05 s ; chaque bombe reçoit une montée R de 1 à 2 s et son
// enregistrement est écrit une fois, avec tTir = tÉclat - R. Tant que la bombe
// monte, l'heure d'éclat est RECALÉE sur la prédiction courante (écart > 6 ms,
// plus de 0,3 s à courir : un saut invisible) - un tempo qui dérive ne la
// décale donc pas. L'éclat est calculé dans le shader à partir de
// tÉclat : il tombe à la frame près sur le temps, quelle que soit la frame où
// la bombe a été planifiée.
//
// LES PARTICULES : AUCUNE SIMULATION. Une bombe = un enregistrement de 5
// texels (texture flottante SLOTS x 5, réécrite seulement quand une bombe est
// planifiée) ; chaque étincelle est un sommet statique (case, étoile, point
// de traînée) dont la position est en FORME CLOSE dans le vertex shader :
// montée balistique qui culmine à l'éclat (y = yl + h(1 - (1 - u)²)), puis
// gravité + frottement linéaire,
//   p(t) = p0 + g t / k + (v0 - g / k)(1 - e^(-kt)) / k.
// Une traînée = la même étoile aux instants t - Δi : des POINTS ESPACÉS, plus
// serrés vers la tête (Δ ∝ i^1.4). Une étoile ne change jamais de
// trajectoire (sauf la croisette, raccordée en position et en vitesse), donc
// l'historique est exact (R16). Pool : 30 720 sommets au tier 2, 15 360 au
// tier 1, 5 760 au tier 0, ordonnés traînée d'abord : le reflet ne dessine
// que les deux premiers points de chaque étoile (drawRange).
//
// LES EFFETS (le nombre d'étoiles vient du tier) :
//   pivoine     sphère de Fibonacci, change de couleur en vol, sans traînée
//   chrysanthème  sphère, traînées dorées, scintille
//   saule       moitié des étoiles, traînées deux fois plus longues, frottement
//               fort : la chute terminale g/k = 0,08 hauteur/s, lente
//   palmier     8 branches épaisses (2-3 brins), traînées quatre fois plus longues
//   anneau      cercle de 2K étoiles incliné (ellipse)
//   croisette   K/4 étoiles qui se divisent en croix à 42 % de leur vie
//   crépitement étoiles ternes puis nuée de micro-flashs de 70 ms
//   comète      7 comètes (3 brins) en éventail depuis une barge, sans montée
//   cœur, étoile  formes planes, rares (4 %)
// Gravité, frottement, scintillement (glitter à 24 Hz par étoile), couleur
// qui change en vol (pivoine A -> B, croisette au partage, saule et
// palmier en refroidissement or -> orange).
//
// LE DÉCOR (un shader plein écran, highp : exception documentée, ses
// hachages et ses rides prennent des coordonnées en pixels qui saturent le
// fp16 de la TV) : ciel nocturne bleu nuit très sombre, étoiles d'1 px,
// collines et ville en silhouette NOIRE (R13 : seul le bord tourné vers un
// éclat s'allume, 2 px), fenêtres rares, flèches d'antenne d'1,5 px. Eau
// sous l'horizon : reflet ridé de la ville, du ciel et de la fumée, colonnes
// de lumière des éclats brisées par la houle, barges de tir. Chaque éclat
// est une lumière (6 au plus, 4 au tier 0) qui éclaire le ciel, la fumée, le
// bord des toits et l'eau. Les éclats eux-mêmes se reflètent : second tracé
// du même pool, retourné sur le plan des barges, ridé, étiré en traits
// verticaux (tiers 1 et 2 ; au tier 0 rides figées, pas de reflet de
// particules : « sans reflet animé »).
//
// LA FUMÉE (R14) : chaque éclat laisse une bouffée (et chaque tir une petite
// au ras de l'eau) qui grossit en √âge, dérive au vent et vit 16 s ; elle
// s'ACCUMULE donc sur la scène. Quads dans une cible en DEMI-RÉSOLUTION
// (prémultipliée, alpha = 1 - exp(-densité k), deux échelles de bruit),
// éclairage diffus calculé aux sommets à partir des mêmes lumières. Le
// bruit suit le repère de la bouffée.
//
// LE RYTHME (R9, ≥ 0,80 : preset party) :
//   TEMPS    éclatement principal, alterné gauche/droite
//   MESURE   figure : salve de 3 à 5 bombes en éventail, symétrique autour de
//            uStageCenter (R19), même type, éclat simultané
//   CROCHES  petites bombes pendant les passages intenses (intensité > 0,62)
//   PHRASE   palette (deux couleurs vdj, écart de teinte ≥ 0,25) et trois types
//   MONTÉE   comètes de plus en plus nombreuses, petites bombes rapides
//   DROP     salve géante de 12 saules dorés + comètes, flash (VdjComfort)
//   CALME    saules lents et pivoines espacées (un temps sur deux)
// SCÈNE de 20 s (R20, lib/scene-clock.js) : BOUQUET sur les 4 dernières
// secondes (deux bombes par croche + comètes sur les temps), puis 2,5 s de
// ciel qui s'apaise. Formée dès la première image : fumée de 10 bouffées
// déjà vieilles et trois bombes déjà ouvertes.
//
// Composition (R19) : barges, figures et ville centrées sur uStageCenter ;
// la pochette n'est protégée que par le masque safeZone, appliqué à chaque
// couche. Exclusions (R6) : aucun éclat dont le rayon dépasse y = 0,78.
// Confort (R10) : luminance estimée à partir des lumières actives, x gain.
//
// Debug : ?pyroeffect=peony|chrys|willow|palm|ring|crossette|crackle|comet|heart|star
// (toutes les bombes de ce type), ?pyrofinale=1 (bouquet permanent).

const SCENE_S = 20;
const FINALE_S = 4;
const CALM_AFTER_S = 2.5;
const HORIZON = 2.05;
const SAFE_FLOOR = 0.35;
const WATER_Y = -0.50;          // the horizon line (safe units, y in [-1, 1])
const MIRROR_Y = -0.535;        // the barges' water plane: the fireworks' mirror
const BARGES = [-0.78, -0.4, 0, 0.4, 0.78];   // x as a fraction of the aspect, about the stage centre
const TIERS = [
  { slots: 24, k: 48, t: 5, puffs: 32, lights: 4, refl: false },
  { slots: 30, k: 64, t: 8, puffs: 48, lights: 6, refl: true },
  { slots: 40, k: 96, t: 8, puffs: 64, lights: 6, refl: true }
];
const TYPES = ['peony', 'chrys', 'willow', 'palm', 'ring', 'crossette', 'crackle', 'comet', 'heart', 'star'];
const TID = {}; TYPES.forEach((n, i) => { TID[n] = i; });
// r radius (safe units, the distance v0 / k a star would travel without gravity),
// life (s), k drag (1/s), g gravity (units/s²), trail (s), light (flash strength), smoke
const SPEC = {
  peony:     { r: 0.37, life: 1.7, k: 2.6, g: 0.10, trail: 0.00, light: 1.0, smoke: 0.9 },
  chrys:     { r: 0.40, life: 2.0, k: 2.4, g: 0.12, trail: 0.30, light: 1.0, smoke: 1.0 },
  willow:    { r: 0.40, life: 4.2, k: 2.0, g: 0.16, trail: 0.95, light: 0.8, smoke: 1.2 },
  palm:      { r: 0.37, life: 2.6, k: 1.4, g: 0.22, trail: 0.60, light: 0.9, smoke: 1.1 },
  ring:      { r: 0.33, life: 1.8, k: 2.6, g: 0.08, trail: 0.00, light: 0.8, smoke: 0.7 },
  crossette: { r: 0.35, life: 2.2, k: 2.2, g: 0.12, trail: 0.24, light: 0.9, smoke: 1.0 },
  crackle:   { r: 0.27, life: 2.0, k: 2.8, g: 0.10, trail: 0.00, light: 0.7, smoke: 1.3 },
  comet:     { r: 1.10, life: 1.6, k: 0.9, g: 0.40, trail: 0.42, light: 0.5, smoke: 0.5 },
  heart:     { r: 0.29, life: 1.9, k: 2.6, g: 0.05, trail: 0.00, light: 0.8, smoke: 0.6 },
  star:      { r: 0.29, life: 1.9, k: 2.6, g: 0.05, trail: 0.00, light: 0.8, smoke: 0.6 }
};
const SHELL_TYPES = ['peony', 'chrys', 'willow', 'palm', 'ring', 'crossette', 'crackle'];
const GOLD = new THREE.Color(1.0, 0.72, 0.32);
const FALLBACK = ['#ff3d6e', '#3fa8ff', '#ffd23f', '#7dff6a'];

function urlParam(name) {
  if (typeof location === 'undefined') return null;
  return new URLSearchParams(location.search).get(name);
}
const clamp01 = (x) => Math.max(0, Math.min(1, x));
const lerp = (a, b, t) => a + (b - a) * t;

// ---------------------------------------------------------------------------
// THE SPARKS. aId = (slot, star k, trail i). Rows of uB: 0 (tLaunch, tBurst,
// type, seed), 1 (launch x, y, burst x, y), 2 (colour A, radius), 3 (colour B,
// star fraction), 4 (life, drag, gravity, trail seconds).
const sparkVertex = (reflect) => `
  precision highp float;
  attribute vec3 aId;
  uniform sampler2D uB;
  uniform float uSlots, uK, uT, uTime, uAspect, uGain, uPxScale, uWaterT;
  uniform vec2 uSafeCenter, uSafeHalfSize;
  uniform float uSafeFeather;
  varying vec3 vCol;
  varying float vCore, vSize, vMask;
  float h1(float n) { return fract(sin(n) * 43758.5453); }
  vec4 row(float s, float r) { return texture2D(uB, vec2((s + 0.5) / uSlots, (r + 0.5) / 5.0)); }
  vec2 motion(vec2 v0, float k, float g, float t) {
    float E = (1.0 - exp(-k * t)) / k;
    vec2 gv = vec2(0.0, -g);
    return gv * t / k + (v0 - gv / k) * E;
  }
  vec2 velAt(vec2 v0, float k, float g, float t) {
    vec2 gv = vec2(0.0, -g);
    return gv / k + (v0 - gv / k) * exp(-k * t);
  }
  void cull() { gl_Position = vec4(2.0, 2.0, 2.0, 1.0); gl_PointSize = 0.0; vCol = vec3(0.0); vCore = 0.0; vSize = 1.0; vMask = 0.0; }
  void main() {
    float s = aId.x, kk = aId.y, ii = aId.z;
    vec4 r0 = row(s, 0.0);
    float tL = r0.x, tB = r0.y, type = r0.z, seed = r0.w;
    if (type < -0.5 || uTime < tL) { cull(); return; }
    vec4 r1 = row(s, 1.0), r2 = row(s, 2.0), r3 = row(s, 3.0), r4 = row(s, 4.0);
    float life = r4.x, kd = r4.y, g = r4.z, trailS = r4.w, rad = r2.w;
    vec2 pos; vec3 col; float bright, size, core = 0.0;
    bool comet = abs(type - 7.0) < 0.5;
    if (uTime < tB && !comet) {
      // THE RISE: a ballistic shell peaking at the burst instant, 3 strands of
      // glittering sparks shed behind it
      if (kk > 2.5) { cull(); return; }
      float R = max(tB - tL, 0.05);
      float back = ii * 0.04;
      float tau = uTime - tL - back;
      if (tau < 0.0) { cull(); return; }
      float u = tau / R;
      pos = vec2(mix(r1.x, r1.z, u), r1.y + (r1.w - r1.y) * (1.0 - (1.0 - u) * (1.0 - u)));
      if (ii > 0.5) {
        // a shed spark: drifts sideways and falls for as long as it has been loose
        pos += vec2((h1(seed * 31.0 + kk * 7.1 + ii * 3.3) - 0.5) * 0.05, -0.5 * back) * back;
      }
      pos.x += (kk - 1.0) * 0.0018;
      float tw = step(0.3, h1(floor(uTime * 24.0) + kk * 5.3 + ii * 1.7 + seed * 13.0));
      bright = ii < 0.5 ? 0.85 : 0.5 * pow(1.0 - ii / uT, 1.4) * tw;
      col = vec3(1.0, 0.74, 0.40);
      size = ii < 0.5 ? 5.0 : 3.0;
      core = ii < 0.5 && kk < 0.5 ? 0.5 : 0.0;
    } else {
      float t = uTime - tB;
      // the star / trail mapping: some types trade stars for longer trails
      float m = 1.0;
      if (abs(type - 2.0) < 0.5 || abs(type - 6.0) < 0.5) m = 2.0;     // willow, crackle
      if (abs(type - 3.0) < 0.5 || comet) m = 4.0;                       // palm, comet
      bool noTrail = type < 0.5 || abs(type - 4.0) < 0.5 || type > 7.5; // peony, ring, heart, star
      float j, N, ti, Tn;
      if (noTrail) {
        if (ii > 1.5) { cull(); return; }
        j = kk + uK * ii; N = uK * 2.0; ti = 0.0; Tn = 1.0;
      } else {
        j = floor(kk / m); N = uK / m; ti = ii * m + mod(kk, m); Tn = uT * m;
      }
      N = floor(N * r3.w + 0.5);
      if (j >= N) { cull(); return; }
      float iFrac = Tn > 1.5 ? ti / (Tn - 1.0) : 0.0;
      float crackle = abs(type - 6.0) < 0.5 ? 1.0 : 0.0;
      float dt = crackle > 0.5 ? 0.0 : trailS * pow(iFrac, 1.4);
      float tt = t - dt;                             // this point's own time since the burst
      if (tt < 0.0) { cull(); return; }
      float hj = h1(j * 1.731 + seed * 17.0);
      float lifeS = life * (0.85 + 0.3 * hj);
      if (tt > lifeS) { cull(); return; }
      vec2 v0; float fz = 1.0;
      vec2 origin = comet ? r1.xy : r1.zw;
      float sp = rad * kd * (1.0 + 0.07 * (h1(j * 3.17 + seed) - 0.5));
      if (abs(type - 3.0) < 0.5) {
        // palm: 8 thick branches, 1-3 strands, biased upward
        float b = mod(j, 8.0), strand = floor(j / 8.0);
        float a = (b + 0.5) / 8.0 * 6.2831853 + seed * 6.2831853;
        vec2 d = vec2(cos(a), sin(a) * 0.85 + 0.22);
        d += (vec2(h1(j * 5.1 + seed), h1(j * 9.7 + seed)) - 0.5) * 0.05 * strand;
        v0 = d * sp;
      } else if (comet) {
        float c = mod(j, 7.0), strand = floor(j / 7.0);
        float ang = (c / 6.0 - 0.5) * (0.8 + 0.5 * h1(seed * 3.0)) + (h1(j * 2.3 + seed) - 0.5) * 0.03 * strand;
        v0 = vec2(sin(ang), cos(ang)) * sp;
      } else if (abs(type - 4.0) < 0.5) {
        float th = j / N * 6.2831853;
        vec3 d3 = vec3(cos(th), sin(th), 0.0);
        float tx = 0.35 + 0.9 * h1(seed * 7.0), rz = seed * 3.14159;
        d3 = vec3(d3.x, d3.y * cos(tx), d3.y * sin(tx));
        v0 = vec2(d3.x * cos(rz) - d3.y * sin(rz), d3.x * sin(rz) + d3.y * cos(rz)) * sp;
        fz = 0.8 + 0.2 * d3.z;
      } else if (abs(type - 8.0) < 0.5) {
        float th = j / N * 6.2831853, s1 = sin(th);
        v0 = vec2(16.0 * s1 * s1 * s1, 13.0 * cos(th) - 5.0 * cos(2.0 * th) - 2.0 * cos(3.0 * th) - cos(4.0 * th) + 2.5) / 17.0 * sp;
      } else if (abs(type - 9.0) < 0.5) {
        // a five-pointed star: straight edges between 10 alternating vertices
        float f = j / N * 10.0, e = floor(f), w = f - e;
        float a0 = e / 10.0 * 6.2831853 + 1.5707963, a1 = (e + 1.0) / 10.0 * 6.2831853 + 1.5707963;
        float q0 = mod(e, 2.0) < 0.5 ? 1.0 : 0.42, q1 = mod(e, 2.0) < 0.5 ? 0.42 : 1.0;
        v0 = mix(vec2(cos(a0), sin(a0)) * q0, vec2(cos(a1), sin(a1)) * q1, w) * sp;
      } else {
        // a Fibonacci sphere, turned by the seed, seen orthographically
        float z = 1.0 - 2.0 * (j + 0.5) / N, rr = sqrt(max(0.0, 1.0 - z * z));
        float ph = j * 2.3999632 + seed * 6.2831853;
        vec3 d3 = vec3(rr * cos(ph), rr * sin(ph), z);
        float ax = seed * 3.1;
        d3 = vec3(d3.x, d3.y * cos(ax) - d3.z * sin(ax), d3.y * sin(ax) + d3.z * cos(ax));
        v0 = d3.xy * sp;
        fz = 0.72 + 0.28 * d3.z;
      }
      float iHead = ti < 0.5 ? 1.0 : 0.0;
      float fade = 1.0 - smoothstep(0.55 * lifeS, lifeS, tt);
      float trailK = iHead > 0.5 ? 1.0 : 0.6 * pow(1.0 - iFrac, 1.3);
      float flash = 1.0 + 1.6 * exp(-tt / 0.05);
      float glitter = step(0.35, h1(floor(uTime * 24.0) + j * 3.7 + ti * 0.61 + seed * 11.0));
      vec3 cA = r2.rgb, cB = r3.rgb;
      float ageK = tt / lifeS;
      size = iHead > 0.5 ? 6.5 : 3.0;
      if (abs(type - 5.0) < 0.5) {
        // crossette: each star splits into a cross at 42 % of its life,
        // position and velocity continuous at the split
        float child = mod(kk, 4.0);
        j = floor(kk / 4.0);
        float ts = 0.42 * life;
        float zc = 1.0 - 2.0 * (j + 0.5) / (uK / 4.0), rc = sqrt(max(0.0, 1.0 - zc * zc));
        float phc = j * 2.3999632 + seed * 6.2831853;
        vec2 vp = vec2(rc * cos(phc), rc * sin(phc)) * sp;
        if (tt < ts) {
          if (child > 0.5) { cull(); return; }
          pos = origin + motion(vp, kd, g, tt);
          col = cA;
        } else {
          vec2 ps = motion(vp, kd, g, ts), vs = velAt(vp, kd, g, ts);
          vec2 e = normalize(vs + vec2(1e-5));
          float ca = child * 1.5707963 + 0.7853982;
          vec2 dir = vec2(e.x * cos(ca) - e.y * sin(ca), e.x * sin(ca) + e.y * cos(ca));
          vec2 v1 = vs * 0.4 + dir * sp * 0.38;
          pos = origin + ps + motion(v1, kd, g, tt - ts);
          col = mix(vec3(1.0), cB, smoothstep(0.0, 0.12, tt - ts));
          flash = 1.0 + 1.4 * exp(-(tt - ts) / 0.05);
        }
        fz = 0.75 + 0.25 * zc;
        col = mix(col, GOLD_C, (1.0 - iHead) * 0.5);
        bright = fade * trailK * flash * mix(1.0, glitter * 1.3, 0.35 * ageK);
      } else if (crackle > 0.5) {
        // crackle: dim gold stars, then a cloud of 70 ms micro-flashes
        float delay = 0.3 + 0.25 * hj;
        float sub = ti;
        if (sub < 0.5) {
          if (tt > delay) { cull(); return; }
          pos = origin + motion(v0, kd, g, tt);
          col = vec3(1.0, 0.7, 0.35); bright = 0.45 * flash; size = 4.0;
        } else {
          float tf = delay + h1(j * 13.1 + sub * 7.7 + seed) * 0.55;
          if (tt < tf || tt > tf + 0.09) { cull(); return; }
          pos = origin + motion(v0, kd, g, tf) + (vec2(h1(j + sub * 3.1 + seed), h1(j * 2.0 + sub + seed * 5.0)) - 0.5) * 0.035;
          col = vec3(1.0, 0.93, 0.78); bright = 1.5 * (1.0 - (tt - tf) / 0.09); size = 4.5; core = 1.0;
        }
      } else {
        pos = origin + motion(v0, kd, g, tt);
        if (type < 0.5 || type > 7.5) {
          // peony (and the shapes): the colour changes in flight
          col = mix(cA, cB, smoothstep(0.35, 0.6, ageK));
          bright = fade * flash;
        } else if (abs(type - 1.0) < 0.5) {
          col = mix(cA, GOLD_C, iHead > 0.5 ? 0.0 : 0.55 + 0.4 * iFrac);
          bright = fade * trailK * flash * mix(1.0, glitter * 1.4, 0.5 * smoothstep(0.3, 0.7, ageK));
        } else if (abs(type - 2.0) < 0.5) {
          // willow: gold cooling to orange, glittering trails
          col = mix(vec3(1.0, 0.82, 0.5), vec3(0.95, 0.42, 0.1), smoothstep(0.1, 0.9, ageK));
          bright = 0.8 * fade * trailK * flash * mix(1.0, glitter * 1.4, 0.55 * (1.0 - iHead));
          size = iHead > 0.5 ? 5.0 : 3.0;
        } else if (abs(type - 3.0) < 0.5) {
          col = mix(mix(GOLD_C, cA, 0.3), vec3(0.95, 0.45, 0.12), iFrac * 0.8 + ageK * 0.3);
          bright = fade * trailK * flash;
          size = iHead > 0.5 ? 9.0 : 4.0;
        } else if (abs(type - 4.0) < 0.5) {
          col = cA; bright = fade * flash;
        } else {
          // comet: a hot head, a gold tail
          col = mix(mix(GOLD_C, cA, 0.45), GOLD_C, iFrac);
          bright = fade * trailK * (1.0 + 0.8 * exp(-tt / 0.08)) * mix(1.0, glitter * 1.3, 0.4 * (1.0 - iHead));
          size = iHead > 0.5 ? 7.0 : 3.5;
        }
        bright *= fz;
      }
      core = max(core, iHead * clamp(bright - 0.55, 0.0, 1.0) * (1.0 - smoothstep(0.0, 0.5, ageK)));
    }
    ${reflect ? `
    // THE REFLECTION: mirrored on the barges' plane, rippled, stretched (the fragment)
    if (pos.y < ${MIRROR_Y.toFixed(3)}) { cull(); return; }
    pos.y = ${(2 * MIRROR_Y).toFixed(3)} - pos.y;
    float depth = ${MIRROR_Y.toFixed(3)} - pos.y;
    float v = 1.0 / (depth + 0.03);
    pos.x += sin(v * 1.3 + uWaterT * 1.7 + sin(pos.x * 6.0 + v * 0.21) * 1.4) * 0.008 * (0.4 + depth * 4.0);
    bright *= 0.34 * exp(-depth * 1.8);
    core = 0.0;
    size *= 1.7;
    ` : `
    if (pos.y < ${MIRROR_Y.toFixed(3)}) { cull(); return; }
    `}
    vec2 q = abs(pos - uSafeCenter) - uSafeHalfSize;
    float sd = length(max(q, 0.0)) + min(max(q.x, q.y), 0.0);
    vMask = mix(${SAFE_FLOOR.toFixed(2)}, 1.0, smoothstep(0.0, uSafeFeather, sd));
    vCol = col * bright * uGain;
    vCore = core * uGain;
    vSize = max(2.0, size * uPxScale);
    gl_PointSize = vSize;
    gl_Position = vec4(pos.x / uAspect, pos.y, 0.0, 1.0);
  }
`.replace(/GOLD_C/g, 'vec3(1.0, 0.72, 0.32)');

const sparkFragment = (reflect) => `
  precision mediump float;
  uniform float uWaterPx;
  varying vec3 vCol;
  varying float vCore, vSize, vMask;
  void main() {
    ${reflect ? 'if (gl_FragCoord.y > uWaterPx) discard;' : 'if (gl_FragCoord.y < uWaterPx) discard;'}
    vec2 pc = gl_PointCoord - 0.5;
    ${reflect ? 'pc.y *= 0.33;' : ''}
    float d = length(pc) * vSize;                    // pixels from the centre
    // R1: a 1 px core, a short halo that reaches 0 well inside the sprite (R12)
    float core = 1.0 - smoothstep(0.4, 1.4, d);
    float halo = exp(-d / 1.5) * (1.0 - smoothstep(vSize * 0.28, vSize * 0.48, d));
    vec3 c = vCol * (0.6 * core + 0.55 * halo) + vec3(1.0, 0.93, 0.82) * core * vCore;
    gl_FragColor = vec4(c * vMask, 1.0);
  }
`;

// ---------------------------------------------------------------------------
// THE SMOKE. aP = (puff, corner x, corner y). uS rows: 0 (t0, x, y, seed),
// 1 (radius, life, strength, -).
const smokeVertex = (nl) => `
  precision highp float;
  attribute vec3 aP;
  uniform sampler2D uS;
  uniform float uPuffs, uTime, uAspect;
  uniform vec2 uWind;
  uniform vec4 uLP[${nl}];
  uniform vec3 uLC[${nl}];
  varying vec2 vQ, vSeed;
  varying vec3 vLight;
  varying float vDens;
  void main() {
    vec4 a = texture2D(uS, vec2((aP.x + 0.5) / uPuffs, 0.25));
    vec4 b = texture2D(uS, vec2((aP.x + 0.5) / uPuffs, 0.75));
    float age = uTime - a.x;
    if (age < 0.0 || age > b.y) { gl_Position = vec4(2.0, 2.0, 2.0, 1.0); vQ = vec2(9.0); vDens = 0.0; vLight = vec3(0.0); vSeed = vec2(0.0); return; }
    float rad = b.x * (1.0 + 0.9 * sqrt(age));
    vec2 c = a.yz + uWind * age + vec2(0.0, 0.01 * age);
    vec2 p = c + aP.yz * rad * 1.4;
    vQ = aP.yz * 1.4;
    vSeed = vec2(a.w, fract(a.w * 1.7));
    vDens = b.z * smoothstep(0.0, 0.5, age) * (1.0 - smoothstep(b.y * 0.5, b.y, age)) / (1.0 + 0.3 * age);
    // diffuse light only (R14): the bursts, plus the city's faint warm glow low down
    vec3 L = vec3(0.020, 0.012, 0.008) * exp(-max(p.y - ${WATER_Y.toFixed(2)}, 0.0) * 3.0);
    for (int i = 0; i < ${nl}; i++) {
      vec2 d = p - uLP[i].xy;
      L += uLC[i] * uLP[i].z * 0.2 / (1.0 + dot(d, d) / (uLP[i].w * uLP[i].w));
    }
    vLight = L;
    gl_Position = vec4(p.x / uAspect, p.y, 0.0, 1.0);
  }
`;
const smokeFragment = `
  precision mediump float;
  uniform sampler2D uNoise;
  varying vec2 vQ, vSeed;
  varying vec3 vLight;
  varying float vDens;
  void main() {
    float r = length(vQ);
    // R12: the density reaches 0 before the quad's edge (1.4)
    float w = 1.0 - smoothstep(0.9, 1.36, r);
    float g = exp(-r * r * 1.4) * w;
    float n1 = texture2D(uNoise, vQ * 0.22 + vSeed).r;              // the veil
    float n2 = texture2D(uNoise, vQ * 0.63 + vSeed.yx * 1.3).r;     // the filaments
    float dens = vDens * g * (0.35 + 0.5 * n1 + 1.1 * n2 * n2);
    float alpha = 1.0 - exp(-dens * 1.8);
    // x4 into the 8-bit target (the bay divides): dim smoke would band in 1/255 steps
    gl_FragColor = vec4(min(vLight * alpha * 4.0, vec3(1.0)), alpha);
  }
`;

// ---------------------------------------------------------------------------
// THE BAY. highp: an exception (see the header).
const quadVertex = `
  varying vec2 vUv;
  void main() { vUv = uv; gl_Position = vec4(position.xy * 2.0, 0.0, 1.0); }
`;
const bayFragment = (nl) => `
  precision highp float;
  uniform sampler2D uSmoke;
  uniform float uAspect, uPx, uTime, uWaterT, uGain;
  uniform vec2 uStage;
  uniform vec4 uLP[${nl}];
  uniform vec3 uLC[${nl}];
  uniform vec4 uBarge;                 // x of the active launch flash, its strength
  uniform vec2 uSafeCenter, uSafeHalfSize;
  uniform float uSafeFeather;
  varying vec2 vUv;
  const float WY = ${WATER_Y.toFixed(3)}, MY = ${MIRROR_Y.toFixed(3)};
  float hs(float n) { return fract(sin(n * 127.1) * 43758.5453); }
  float hs2(vec2 p) { return fract(sin(dot(p, vec2(127.1, 311.7))) * 43758.5453); }
  // a building row: cells of width cw, heights under an envelope centred on the stage
  vec3 rectOf(float c, float cw, float layer) {
    float h = hs(c + layer * 91.7), h2 = hs(c * 1.37 + 71.3 + layer), h3 = hs(c * 0.71 + 13.7 + layer);
    float xc = (c + 0.5) * cw;
    float env = layer < 0.5
      ? 0.030 + 0.115 * exp(-xc * xc / (0.30 * uAspect * uAspect)) + 0.03 * exp(-(xc + 0.55 * uAspect) * (xc + 0.55 * uAspect) / 0.04)
      : 0.050 + 0.150 * exp(-xc * xc / (0.12 * uAspect * uAspect));
    float top = WY + 0.006 + env * (0.30 + 0.70 * h * h);
    if (layer > 0.5 && h2 < 0.45) top = WY - 1.0;                   // the back row is sparse towers
    float w = cw * (0.62 + 0.38 * h2);
    float x0 = c * cw + (cw - w) * h3;
    return vec3(x0, x0 + w, top);
  }
  // signed distance (units) to the silhouette (hills + two rows of buildings
  // + spires), with the outward normal of the nearest edge
  vec3 sil(vec2 p) {
    float x = p.x - uStage.x;
    // the hills: low in the middle, rising to frame the bay
    float hy = WY + 0.028 + 0.016 * sin(x * 2.3 + 1.0) + 0.010 * sin(x * 5.1 + 2.0) + 0.13 * smoothstep(0.42 * uAspect, 1.1 * uAspect, abs(x));
    float best = (p.y - hy) * 0.95;
    vec2 nrm = vec2(0.0, 1.0);
    for (int layer = 0; layer < 2; layer++) {
      float cw = layer == 0 ? 0.034 : 0.058;
      float c0 = floor(x / cw);
      for (int k = -1; k <= 1; k++) {
        vec3 r = rectOf(c0 + float(k), cw, float(layer));
        float dx = max(r.x - x, x - r.y), dy = p.y - r.z;
        float d = length(max(vec2(dx, dy), 0.0)) + min(max(dx, dy), 0.0);
        if (d < best) { best = d; nrm = dx > dy ? vec2(x < 0.5 * (r.x + r.y) ? -1.0 : 1.0, 0.0) : vec2(0.0, 1.0); }
        // a spire on the tall ones
        if (layer == 0 && hs(c0 + float(k) + 5.0) > 0.86 && r.z > WY + 0.09) {
          float xm = 0.5 * (r.x + r.y);
          float sdx = abs(x - xm) - 0.75 * uPx, sdy = p.y - (r.z + 0.04);
          float ds = length(max(vec2(sdx, sdy), 0.0)) + min(max(sdx, sdy), 0.0);
          if (ds < best) { best = ds; nrm = sdx > sdy ? vec2(x < xm ? -1.0 : 1.0, 0.0) : vec2(0.0, 1.0); }
        }
      }
    }
    return vec3(best, nrm);
  }
  // the lit windows of the front row (dim, under the bloom threshold)
  float windows(vec2 p) {
    float x = p.x - uStage.x;
    float cw = 0.034, c = floor(x / cw);
    vec3 r = rectOf(c, cw, 0.0);
    if (x < r.x + 0.003 || x > r.y - 0.003 || p.y > r.z - 0.006) return 0.0;
    vec2 g = vec2((x - r.x) / 0.0062, (r.z - p.y) / 0.0095);
    vec2 cell = floor(g), f = fract(g);
    float on = step(0.86 - 0.08 * hs(c + 3.0), hs2(cell + c * 17.0));
    vec2 e = min(f, 1.0 - f) * vec2(0.0062, 0.0095) / uPx;          // pixels to the window's edge
    float inside = smoothstep(0.6, 1.4, min(e.x, e.y));
    return on * inside * (0.5 + 0.5 * hs2(cell * 1.3 + c));
  }
  vec3 skyAt(vec2 p) {
    float y = p.y;
    vec3 col = mix(vec3(0.0035, 0.0055, 0.016), vec3(0.0, 0.0, 0.0015), smoothstep(WY, 0.7, y));
    col += vec3(0.016, 0.009, 0.004) * exp(-max(y - WY, 0.0) / 0.07);   // the city's glow
    for (int i = 0; i < ${nl}; i++) {
      vec2 d = p - uLP[i].xy;
      col += uLC[i] * uLP[i].z * 0.03 / (1.0 + dot(d, d) / 0.12);
    }
    return col;
  }
  void main() {
    vec2 p = (vUv * 2.0 - 1.0) * vec2(uAspect, 1.0);
    vec3 col;
    if (p.y >= WY) {
      col = skyAt(p);
      // the stars: one per 9 px cell at most, 1 px, slow twinkle, above the city
      vec2 cell = floor(gl_FragCoord.xy / 9.0);
      float hst = hs2(cell);
      if (hst > 0.95 && p.y > WY + 0.2) {
        vec2 sp = (cell + 0.2 + 0.6 * vec2(hs2(cell + 7.0), hs2(cell + 13.0))) * 9.0;
        float d = length(gl_FragCoord.xy - sp);
        col += vec3(0.8, 0.85, 1.0) * (1.0 - smoothstep(0.3, 1.2, d)) * (0.12 + 0.2 * hs2(cell + 3.0)) * (0.75 + 0.25 * sin(uTime * 1.3 + hst * 40.0));
      }
      // the smoke, premultiplied, over the sky
      vec4 sm = texture2D(uSmoke, vUv);
      col = col * (1.0 - sm.a) + sm.rgb * 0.25;
      // the silhouette: black (R13), its edge facing a burst lights up over 2 px
      vec3 s = sil(p);
      float dpx = s.x / uPx;
      float cov = clamp(0.5 - dpx, 0.0, 1.0);
      if (cov > 0.0) {
        vec3 rim = vec3(0.0);
        float edge = 1.0 - smoothstep(1.5, 2.5, -dpx);
        if (edge > 0.0) {
          for (int i = 0; i < ${nl}; i++) {
            vec2 L = uLP[i].xy - p;
            float dl = length(L);
            float f = max(0.0, dot(s.yz, L / max(dl, 1e-4)) - 0.35) / 0.65;
            rim += uLC[i] * uLP[i].z * f * f / (1.0 + dl * dl * 4.0);
          }
          rim *= 0.4 * edge;
        }
        col = mix(col, rim + vec3(1.0, 0.72, 0.42) * 0.22 * windows(p), cov);
      }
    } else {
      // THE WATER: perspective ripples (compressed toward the horizon)
      float depth = WY - p.y;
      float v = 1.0 / (depth + 0.03);
      float wave = sin(v * 11.0 + uWaterT * 1.7 + sin(p.x * 5.0 + v * 0.7) * 1.2) * 0.5 + sin(v * 23.0 - uWaterT * 2.3 + p.x * 9.0) * 0.25;
      float xr = p.x + wave * 0.006 * (0.3 + depth * 3.0);
      vec2 pm = vec2(xr, WY + depth * 0.94);
      vec3 refl = skyAt(pm);
      vec2 muv = vec2((xr / uAspect + 1.0) * 0.5, (pm.y + 1.0) * 0.5);
      vec4 sm = texture2D(uSmoke, muv);
      refl = refl * (1.0 - sm.a) + sm.rgb * 0.15;
      vec3 s = sil(pm);
      float cov = clamp(0.5 - s.x / uPx, 0.0, 1.0);
      refl = mix(refl, vec3(1.0, 0.72, 0.42) * 0.22 * windows(pm), cov);
      float fres = mix(0.62, 0.34, smoothstep(0.0, 0.45, depth));
      col = vec3(0.003, 0.006, 0.014) + refl * fres;
      // the bursts' light on the water: columns broken by the swell
      // horizontal glints: the crests facing the light, one per ripple row
      float shimmer = smoothstep(0.3, 1.0, 0.5 + 0.5 * sin(v * 17.0 - uWaterT * 2.0 + wave * 2.5 + sin(p.x * 31.0) * 0.8));
      for (int i = 0; i < ${nl}; i++) {
        float dx = abs(xr - uLP[i].x);
        col += uLC[i] * uLP[i].z * 0.3 * exp(-dx / (0.012 + depth * 0.12)) * exp(-depth * 1.6) * shimmer;
      }
      // the barges: black hulls on the water plane, a launch flash on the active one
      float bx = p.x - uStage.x;
      for (int b = 0; b < 5; b++) {
        float cx = b == 0 ? ${BARGES[0].toFixed(2)} : b == 1 ? ${BARGES[1].toFixed(2)} : b == 2 ? 0.0 : b == 3 ? ${BARGES[3].toFixed(2)} : ${BARGES[4].toFixed(2)};
        vec2 q = abs(vec2(bx - cx * uAspect, p.y - (MY + 0.002))) - vec2(0.045, 0.006);
        float d = (length(max(q, 0.0)) + min(max(q.x, q.y), 0.0)) / uPx;
        col *= clamp(d + 0.5, 0.0, 1.0);
      }
      vec2 fq = vec2(p.x - uBarge.x, (p.y - MY) * 3.0);
      col += vec3(1.0, 0.6, 0.3) * uBarge.z * 0.5 * exp(-dot(fq, fq) / 0.0015);
    }
    col *= uGain;
    vec2 q = abs(p - uSafeCenter) - uSafeHalfSize;
    float sd = length(max(q, 0.0)) + min(max(q.x, q.y), 0.0);
    col *= mix(${SAFE_FLOOR.toFixed(2)}, 1.0, smoothstep(0.0, uSafeFeather, sd));
    col += (hs2(gl_FragCoord.xy + fract(uTime)) - 0.5) / 255.0;
    gl_FragColor = vec4(max(col, 0.0), 1.0);
  }
`;

// a tileable value-noise fbm, 128², made once
let _noiseTex = null;
function noiseTexture() {
  if (_noiseTex) return _noiseTex;
  const N = 128, data = new Uint8Array(N * N * 4);
  let seed = 9001;
  const rnd = () => { seed = (seed * 16807) % 2147483647; return seed / 2147483647; };
  const octs = [8, 16, 32].map((L) => { const g = new Float32Array(L * L); for (let i = 0; i < g.length; i++) g[i] = rnd(); return { L, g }; });
  const sm = (t) => t * t * (3 - 2 * t);
  for (let y = 0; y < N; y++) for (let x = 0; x < N; x++) {
    let v = 0, amp = 0.55, tot = 0;
    for (const o of octs) {
      const fx = x / N * o.L, fy = y / N * o.L, ix = Math.floor(fx), iy = Math.floor(fy), tx = sm(fx - ix), ty = sm(fy - iy);
      const at = (a, b) => o.g[((b % o.L) * o.L) + (a % o.L)];
      const a = lerp(at(ix, iy), at(ix + 1, iy), tx), b = lerp(at(ix, iy + 1), at(ix + 1, iy + 1), tx);
      v += lerp(a, b, ty) * amp; tot += amp; amp *= 0.5;
    }
    const c = Math.round(clamp01(v / tot) * 255), k = (y * N + x) * 4;
    data[k] = data[k + 1] = data[k + 2] = c; data[k + 3] = 255;
  }
  const t = new THREE.DataTexture(data, N, N, THREE.RGBAFormat, THREE.UnsignedByteType);
  t.wrapS = t.wrapT = THREE.RepeatWrapping; t.magFilter = t.minFilter = THREE.LinearFilter; t.generateMipmaps = false; t.needsUpdate = true;
  _noiseTex = t;
  return t;
}

export class PyromusicalVisualizer extends Visualizer {
  constructor(options) {
    super();
    const o = options || {};
    this._tier = o.qualityTier != null ? Math.max(0, Math.min(2, o.qualityTier)) : 1;
    this._L = TIERS[this._tier];
    const fe = urlParam('pyroeffect');
    this._forceType = fe && TID[fe] != null ? fe : (fe === 'chrysanthemum' ? 'chrys' : null);
    this._forceFinale = urlParam('pyrofinale') === '1';
    this._post = new VdjPost({ bloomStrength: 0.6, bloomThreshold: 0.78, grain: 0.0, vignette: 0.3, toneMapping: THREE.NoToneMapping, toneMappingExposure: 1 });
    this._comfort = new VdjComfort();
    this._clock = new SceneClock({ period: SCENE_S, start: 'random' });
    this._adapt = new Adapt();
  }

  get postProcessing() { return this._post.value; }
  _rnd() { this._seed = (this._seed * 16807) % 2147483647; return this._seed / 2147483647; }

  init(scene) {
    this._scene = scene;
    this._seed = 1234 + Math.floor(Math.random() * 1000);
    const L = this._L;
    const NL = L.lights;
    // the burst records
    this._bData = new Float32Array(L.slots * 5 * 4);
    for (let s = 0; s < L.slots; s++) this._bData[s * 4 + 2] = -1;             // type -1: empty
    this._bTex = new THREE.DataTexture(this._bData, L.slots, 5, THREE.RGBAFormat, THREE.FloatType);
    this._bTex.magFilter = this._bTex.minFilter = THREE.NearestFilter; this._bTex.generateMipmaps = false; this._bTex.needsUpdate = true;
    this._slotEnd = new Float64Array(L.slots).fill(-1e9);
    this._slotX = new Float64Array(L.slots).fill(NaN);                     // the beat coordinate aimed at
    this._slotInfo = new Array(L.slots).fill(null);
    // the smoke records
    this._sData = new Float32Array(L.puffs * 2 * 4);
    for (let i = 0; i < L.puffs; i++) this._sData[i * 4] = -1e6;
    this._sTex = new THREE.DataTexture(this._sData, L.puffs, 2, THREE.RGBAFormat, THREE.FloatType);
    this._sTex.magFilter = this._sTex.minFilter = THREE.NearestFilter; this._sTex.generateMipmaps = false; this._sTex.needsUpdate = true;
    this._puffEnd = new Float64Array(L.puffs).fill(-1e9);

    // the lights, shared by the bay and the smoke
    this._lp = []; this._lc = [];
    for (let i = 0; i < NL; i++) { this._lp.push(new THREE.Vector4(0, 0, 0, 0.3)); this._lc.push(new THREE.Color(0, 0, 0)); }

    // THE SPARKS: one static attribute, trail-major (the reflection draws i < 2)
    const n = L.slots * L.k * L.t;
    const id = new Float32Array(n * 3);
    let w = 0;
    for (let i = 0; i < L.t; i++) for (let s = 0; s < L.slots; s++) for (let k = 0; k < L.k; k++) { id[w++] = s; id[w++] = k; id[w++] = i; }
    const attr = new THREE.BufferAttribute(id, 3);
    const posAttr = new THREE.BufferAttribute(new Float32Array(n * 3), 3);
    this._geo = new THREE.BufferGeometry();
    this._geo.setAttribute('position', posAttr); this._geo.setAttribute('aId', attr);
    const sparkU = (refl) => ({
      uB: { value: this._bTex }, uSlots: { value: L.slots }, uK: { value: L.k }, uT: { value: L.t },
      uTime: { value: 0 }, uAspect: safeZoneUniforms.uAspect, uGain: { value: 1 }, uPxScale: { value: 1 }, uWaterT: { value: 0 },
      uWaterPx: { value: 0 },
      uSafeCenter: safeZoneUniforms.uSafeCenter, uSafeHalfSize: safeZoneUniforms.uSafeHalfSize, uSafeFeather: safeZoneUniforms.uSafeFeather
    });
    const mkSpark = (refl) => new THREE.ShaderMaterial({
      uniforms: sparkU(refl), vertexShader: sparkVertex(refl), fragmentShader: sparkFragment(refl),
      transparent: true, blending: THREE.AdditiveBlending, depthTest: false, depthWrite: false
    });
    this._sparkMat = mkSpark(false);
    this._sparks = new THREE.Points(this._geo, this._sparkMat);
    this._sparks.frustumCulled = false; this._sparks.renderOrder = -990;
    scene.add(this._sparks);
    if (L.refl) {
      this._geoR = new THREE.BufferGeometry();
      this._geoR.setAttribute('position', posAttr); this._geoR.setAttribute('aId', attr);
      this._geoR.setDrawRange(0, L.slots * L.k * 2);
      this._reflMat = mkSpark(true);
      this._refl = new THREE.Points(this._geoR, this._reflMat);
      this._refl.frustumCulled = false; this._refl.renderOrder = -995;
      scene.add(this._refl);
    }

    // THE SMOKE: quads into a half-resolution target
    const np = L.puffs;
    const sp = new Float32Array(np * 4 * 3), idx = [];
    const corners = [[-1, -1], [1, -1], [1, 1], [-1, 1]];
    for (let i = 0; i < np; i++) {
      for (let c = 0; c < 4; c++) { const o = (i * 4 + c) * 3; sp[o] = i; sp[o + 1] = corners[c][0]; sp[o + 2] = corners[c][1]; }
      const b = i * 4; idx.push(b, b + 1, b + 2, b, b + 2, b + 3);
    }
    this._smokeGeo = new THREE.BufferGeometry();
    this._smokeGeo.setAttribute('position', new THREE.BufferAttribute(new Float32Array(np * 4 * 3), 3));
    this._smokeGeo.setAttribute('aP', new THREE.BufferAttribute(sp, 3));
    this._smokeGeo.setIndex(idx);
    this._smokeMat = new THREE.ShaderMaterial({
      uniforms: {
        uS: { value: this._sTex }, uPuffs: { value: np }, uTime: { value: 0 }, uAspect: safeZoneUniforms.uAspect,
        uWind: { value: new THREE.Vector2(0.012, 0) }, uLP: { value: this._lp }, uLC: { value: this._lc }, uNoise: { value: noiseTexture() }
      },
      vertexShader: smokeVertex(NL), fragmentShader: smokeFragment,
      transparent: true, depthTest: false, depthWrite: false,
      blending: THREE.CustomBlending, blendEquation: THREE.AddEquation,
      blendSrc: THREE.OneFactor, blendDst: THREE.OneMinusSrcAlphaFactor,
      blendSrcAlpha: THREE.OneFactor, blendDstAlpha: THREE.OneMinusSrcAlphaFactor
    });
    this._smoke = new THREE.Mesh(this._smokeGeo, this._smokeMat);
    this._smoke.frustumCulled = false;
    this._priv = new THREE.Scene(); this._priv.add(this._smoke);
    this._privCam = new THREE.Camera();

    // THE BAY
    this._bayMat = new THREE.ShaderMaterial({
      uniforms: {
        uSmoke: { value: null }, uAspect: safeZoneUniforms.uAspect, uPx: { value: 2 / 1080 }, uTime: { value: 0 }, uWaterT: { value: 0 }, uGain: { value: 1 },
        uStage: { value: new THREE.Vector2() }, uLP: { value: this._lp }, uLC: { value: this._lc }, uBarge: { value: new THREE.Vector4() },
        uSafeCenter: safeZoneUniforms.uSafeCenter, uSafeHalfSize: safeZoneUniforms.uSafeHalfSize, uSafeFeather: safeZoneUniforms.uSafeFeather
      },
      vertexShader: quadVertex, fragmentShader: bayFragment(NL), depthTest: false, depthWrite: false
    });
    this._display = new THREE.Mesh(new THREE.PlaneGeometry(1, 1), this._bayMat);
    this._display.frustumCulled = false; this._display.renderOrder = -1000;
    const self = this;
    this._size = new THREE.Vector2();
    this._display.onBeforeRender = function (renderer) { self._render(renderer); };
    scene.add(this._display);

    // state
    this._time = 0; this._free = 0; this._beats = 0; this._bars = 0; this._phrases = 0; this._secondsBeat = 60 / 120;
    this._phaseBeat = null; this._phaseBar = null; this._phrasePhase = null; this._beatPhase = 0; this._barPhase = 0;
    this._lastPlanned = -1e9; this._build = 0; this._intensity = 0.6; this._dropAt = null; this._lastDropKey = null;
    this._side = 1; this._flash = 0; this._exposure = 1; this._bargeFlash = new THREE.Vector4();
    this._stats = { planned: 0, dropped: 0, bursts: [] };
    this._paletteRef = undefined; this._vivid = FALLBACK.slice();
    this._colA = new THREE.Color(); this._colB = new THREE.Color();
    this._newPhrase();
    if (this._forceFinale) this._clock.restart((SCENE_S - FINALE_S) / SCENE_S);
    this._prefill();
    this._updateLights();
  }

  // R20: the scene is formed at the first frame - old smoke, three bursts already open
  _prefill() {
    const A = safeZoneUniforms.uAspect.value || 16 / 9;
    for (let i = 0; i < 10; i++) {
      const x = (this._rnd() * 1.6 - 0.8) * A, y = 0.05 + this._rnd() * 0.45;
      this._addPuff(-2 - this._rnd() * 12, x, y, 0.12 + 0.08 * this._rnd(), 0.3);
    }
    const pre = [['willow', -0.9, -0.45], ['peony', -0.35, 0.4], ['chrys', -0.15, 0.05]];
    for (const [type, age, xf] of pre) {
      const t = this._forceType || type;
      this._fire({ type: t, tb: age, R: 1.4, x: xf * A, y: 0.25 + 0.15 * this._rnd(), scale: 1, X: NaN });
    }
  }

  _newPhrase() {
    // two palette colours at least 0.25 apart in hue (R4), three shell types
    const v = this._vivid;
    const a = Math.floor(this._rnd() * v.length);
    vdjColor(v[a], this._colA);
    let found = false;
    const hA = { h: 0, s: 0, l: 0 }, hB = { h: 0, s: 0, l: 0 };
    this._colA.getHSL(hA);
    for (let k = 1; k < v.length && !found; k++) {
      vdjColor(v[(a + k) % v.length], this._colB); this._colB.getHSL(hB);
      const dh = Math.min(Math.abs(hA.h - hB.h), 1 - Math.abs(hA.h - hB.h));
      if (dh >= 0.25) found = true;
    }
    if (!found) this._colB.setHSL((hA.h + 0.42) % 1, 0.9, 0.55);
    const pool = SHELL_TYPES.slice();
    this._phraseTypes = [];
    for (let i = 0; i < 3; i++) this._phraseTypes.push(pool.splice(Math.floor(this._rnd() * pool.length), 1)[0]);
  }

  _pickType(pool) {
    if (this._forceType) return this._forceType;
    if (this._rnd() < 0.04) return this._rnd() < 0.5 ? 'heart' : 'star';
    const p = pool || this._phraseTypes;
    return p[Math.floor(this._rnd() * p.length)];
  }

  // a free slot; for the drop (force), else the burst nearest its end - never one still to come
  _allocSlot(force) {
    let best = -1;
    for (let s = 0; s < this._L.slots; s++) if (this._slotEnd[s] < this._time && (best < 0 || this._slotEnd[s] < this._slotEnd[best])) best = s;
    if (best < 0 && force) {
      for (let s = 0; s < this._L.slots; s++) {
        const f = this._slotInfo[s];
        if (f && f.tb < this._time - 0.5 && (best < 0 || this._slotEnd[s] < this._slotEnd[best])) best = s;
      }
    }
    return best;
  }

  _addPuff(t0, x, y, r, strength) {
    const L = this._L;
    let best = 0;
    for (let i = 1; i < L.puffs; i++) if (this._puffEnd[i] < this._puffEnd[best]) best = i;
    const life = 13 + 5 * this._rnd();
    const d = this._sData, o = best * 4, o2 = (L.puffs + best) * 4;
    d[o] = t0; d[o + 1] = x; d[o + 2] = y; d[o + 3] = this._rnd() * 10;
    d[o2] = r; d[o2 + 1] = life; d[o2 + 2] = strength; d[o2 + 3] = 0;
    this._puffEnd[best] = t0 + life;
    this._sTex.needsUpdate = true;
  }

  // one shell (or comet fan): {type, tb (burst time), R (rise), x, y, scale, X, frac, gold, launchX}
  _fire(f) {
    const L = this._L, sp = SPEC[f.type];
    const comet = f.type === 'comet';
    const tLaunch = comet ? f.tb : f.tb - f.R;
    const s = this._allocSlot(!!f.force);
    if (s < 0) { this._stats.dropped++; return false; }
    const A = safeZoneUniforms.uAspect.value || 16 / 9;
    const st = safeZoneUniforms.uStageCenter.value;
    const scale = f.scale || 1;
    const rad = sp.r * scale;
    // the barge nearest the burst's x (comets carry their own)
    let lx = f.launchX;
    if (lx == null) {
      let bd = 1e9;
      for (const b of BARGES) { const bx = st.x + b * A; if (Math.abs(bx - f.x) < bd) { bd = Math.abs(bx - f.x); lx = bx; } }
      lx += (this._rnd() - 0.5) * 0.04;
    }
    const ly = MIRROR_Y + 0.004;
    const d = this._bData, row = (r) => (r * L.slots + s) * 4;
    const seed = this._rnd();
    let cA = this._colA, cB = this._colB;
    if (f.gold || f.type === 'willow') { cA = GOLD; cB = GOLD; }
    else if (this._rnd() < 0.5) { const t = cA; cA = cB; cB = t; }
    let o = row(0); d[o] = tLaunch; d[o + 1] = f.tb; d[o + 2] = TID[f.type]; d[o + 3] = seed;
    o = row(1); d[o] = lx; d[o + 1] = ly; d[o + 2] = comet ? lx : f.x; d[o + 3] = comet ? ly : f.y;
    o = row(2); d[o] = cA.r; d[o + 1] = cA.g; d[o + 2] = cA.b; d[o + 3] = rad;
    o = row(3); d[o] = cB.r; d[o + 1] = cB.g; d[o + 2] = cB.b; d[o + 3] = f.frac != null ? f.frac : 1;
    o = row(4); d[o] = sp.life; d[o + 1] = sp.k; d[o + 2] = sp.g * (comet ? 1 : Math.sqrt(scale)); d[o + 3] = sp.trail;
    this._bTex.needsUpdate = true;
    this._slotEnd[s] = f.tb + sp.life * 1.15 + sp.trail + 0.05;
    this._slotX[s] = f.X;
    this._slotInfo[s] = { type: f.type, tb: f.tb, tL: tLaunch, x: comet ? lx : f.x, y: comet ? ly : f.y, lx, light: sp.light * Math.min(1.3, scale + 0.15), col: cA, comet };
    // the smoke it leaves: a puff where it bursts, a small one on the water at the launch
    if (!comet) this._addPuff(f.tb, f.x, f.y, rad * 0.4, 0.3 * sp.smoke * Math.min(1, 0.4 + scale));
    this._addPuff(tLaunch, lx, MIRROR_Y + 0.03, 0.05, 0.4);
    this._stats.planned++;
    const rec = { tb: f.tb, X: f.X, type: f.type };
    this._slotInfo[s].rec = rec;
    this._stats.bursts.push(rec);
    if (this._stats.bursts.length > 200) this._stats.bursts.shift();
    return true;
  }

  // ------------------------------------------------------------------------
  // THE CADENCE: every future eighth is planned once, as soon as it is 2.05 s away
  _plan() {
    const T = this._time, spb = this._secondsBeat;
    const Bc = this._beats + this._beatPhase;
    let X = Math.floor(Bc * 2 + 1) / 2;
    for (; ; X += 0.5) {
      const te = T + (X - Bc) * spb;
      if (te - T > HORIZON) break;
      if (X <= this._lastPlanned + 1e-6) continue;
      this._lastPlanned = X;
      this._planEighth(X, te, T, Bc);
    }
  }

  _planEighth(X, te, T, Bc) {
    const A = safeZoneUniforms.uAspect.value || 16 / 9;
    const st = safeZoneUniforms.uStageCenter.value;
    const ahead = te - T;
    // at the start (and after a jump in the grid) an eighth too close for a real rise is let go
    if (ahead < 0.9) return;
    const isBeat = Math.abs(X - Math.round(X)) < 1e-6;
    // the pool's occupancy decides what is worth a slot: drop > figure > beat
    // > comets > eighths (a tier with fewer slots fires less, never late)
    let used = 0;
    for (let s = 0; s < this._L.slots; s++) if (this._slotEnd[s] >= T) used++;
    const occ = used / this._L.slots;
    const curInBar = Math.floor(this._barPhase * 4 + 1e-3) % 4;
    const inBar = isBeat ? (((curInBar + Math.round(X) - this._beats) % 4) + 4) % 4 : -1;
    const isBar = inBar === 0;
    const sceneT = (this._clock.elapsed + ahead) % SCENE_S;
    const finale = this._forceFinale || sceneT >= SCENE_S - FINALE_S;
    const settle = !this._forceFinale && sceneT < CALM_AFTER_S;
    const E = this._intensity, build = this._build;
    const calm = E < 0.36 || this._calmMode;
    const R = () => Math.min(ahead, 1.0 + this._rnd()), RN = () => Math.min(ahead, 1.0 + 0.45 * this._rnd());
    // safe y for a radius: the burst stays under y = 0.78 (R6) and above the city
    const yFor = (rad, lo, hi) => { const top = 0.78 - rad; return Math.max(-0.12, Math.min(top, lerp(lo, hi, this._rnd()))); };
    const xIn = (x, rad) => Math.max(st.x - A * 0.96 + rad, Math.min(st.x + A * 0.96 - rad, x));
    const shell = (type, x, y, scale, extra) => this._fire(Object.assign({ type, tb: te, R: R(), x, y, scale, X }, extra || {}));
    // n = 1..5: one fan from the centre barge, or three (outer pair + centre)
    // whose fans fill up with n - one record per barge, never more than three
    const comets = (n) => {
      if (occ > 0.75) return;
      const barges = n <= 1 ? [2] : n === 2 ? [0, 4] : [0, 2, 4];
      for (const b of barges) {
        this._fire({ type: 'comet', tb: te, R: 0, x: 0, y: 0, launchX: st.x + BARGES[b] * A, X, gold: this._rnd() < 0.5, frac: Math.min(1, 0.45 + 0.11 * n) });
      }
    };

    // DROP: twelve gold willows in a giant fan, comets, a flash
    if (this._dropAt != null && Math.abs(te - this._dropAt) <= spbHalf(this._secondsBeat) && isBeat) {
      this._dropAt = null;
      for (let i = 0; i < 12; i++) {
        const u = (i + 0.5) / 12 * 2 - 1;
        const rad = SPEC.willow.r * 0.9;
        this._fire({ force: true, type: this._forceType || 'willow', tb: te, R: RN(), x: xIn(st.x + u * A * 0.82, rad), y: Math.min(0.78 - rad, 0.18 + 0.2 * (1 - u * u)), scale: 0.9, X, gold: true });
      }
      comets(3);
      this._pendingFlashAt = te;
      return;
    }
    if (finale) {
      // THE FINALE: two shells on every beat, one on every eighth between,
      // comets on the beats - short-lived types, so the pool keeps up
      for (let i = 0; i < (isBeat ? 2 : occ < 0.8 ? 1 : 0); i++) {
        const type = this._pickType(['peony', 'chrys', 'crackle', 'palm', 'crossette', 'ring']);
        const scale = 0.7 + 0.35 * this._rnd();
        const rad = SPEC[type].r * scale;
        shell(type, xIn(st.x + (this._rnd() * 2 - 1) * A * 0.85, rad), yFor(rad, -0.05, 0.42), scale);
      }
      if (isBeat) comets(isBar ? 5 : 2);
      return;
    }
    if (settle) {
      if (isBar) { const type = this._pickType(['willow', 'peony']); shell(type, st.x + (this._rnd() - 0.5) * A * 0.6, yFor(SPEC[type].r, 0.1, 0.35), 0.85); }
      return;
    }
    if (calm) {
      // slow willows and spaced peonies: one beat in two
      if (isBeat && Math.round(X) % 2 === 0) {
        const type = this._forceType || (Math.round(X) % 4 === 0 ? 'willow' : 'peony');
        this._side = -this._side;
        const rad = SPEC[type].r;
        shell(type, xIn(st.x + this._side * A * (0.25 + 0.4 * this._rnd()), rad), yFor(rad, 0.05, 0.38), 0.95);
      }
      return;
    }
    if (isBar) {
      // THE FIGURE: 3 to 5 shells in a fan, symmetric about the stage centre (R19)
      const n = Math.max(3, Math.min(E > 0.75 ? 5 : E > 0.55 ? 4 : 3, this._L.slots - used - 2));
      const type = this._pickType();
      const scale = n >= 5 ? 0.72 : n === 4 ? 0.8 : 0.9;
      const rad = SPEC[type].r * scale;
      const span = Math.min(A * 0.9 - rad, rad * 1.7 * (n - 1) / 2);
      const seedSide = this._rnd();
      for (let i = 0; i < n; i++) {
        const u = n === 1 ? 0 : (i / (n - 1)) * 2 - 1;
        this._fire({ type, tb: te, R: 1.25 + 0.5 * seedSide, x: st.x + u * span, y: Math.min(0.78 - rad, 0.12 + 0.16 * (1 - u * u)), scale, X, launchX: st.x + u * 0.12 * A });
      }
      if (build > 0.25) comets(Math.min(5, 1 + Math.round(build * 4)));
      return;
    }
    if (isBeat) {
      // THE BEAT: one main burst, alternating sides
      const type = this._pickType();
      this._side = -this._side;
      const scale = 0.85 + 0.3 * E;
      const rad = SPEC[type].r * scale;
      shell(type, xIn(st.x + this._side * A * (0.3 + 0.38 * this._rnd()), rad), yFor(rad, 0.0, 0.4), scale);
      // THE BUILD: more and more comets
      if (build > 0.15) comets(Math.min(5, Math.round(1 + build * 4)));
      return;
    }
    // THE EIGHTHS: small fast bombs when the passage is intense or building
    if ((E > 0.62 || build > 0.3) && occ < 0.6) {
      const n = build > 0.6 && occ < 0.4 ? 2 : 1;
      for (let i = 0; i < n; i++) {
        const type = this._forceType || (this._rnd() < 0.6 ? 'peony' : 'crackle');
        const scale = 0.42 + 0.12 * this._rnd();
        const rad = SPEC[type].r * scale;
        this._fire({ type, tb: te, R: RN(), x: xIn(st.x + (this._rnd() * 2 - 1) * A * 0.75, rad), y: yFor(rad, 0.0, 0.5), scale, X, frac: 0.6 });
      }
    }
  }

  // keep every rising shell on the CURRENT prediction of its beat
  _resync() {
    const T = this._time, spb = this._secondsBeat, Bc = this._beats + this._beatPhase;
    const L = this._L, d = this._bData;
    let dirty = false;
    for (let s = 0; s < L.slots; s++) {
      const X = this._slotX[s], info = this._slotInfo[s];
      if (!info || !Number.isFinite(X)) continue;
      const tb = d[s * 4 + 1];
      if (tb - T < 0.3) continue;
      const pred = T + (X - Bc) * spb;
      if (Math.abs(pred - tb) > 0.006 && Math.abs(pred - tb) < 0.25) {
        const o = s * 4;
        if (info.comet) d[o] = pred;
        d[o + 1] = pred; info.tb = pred; if (info.rec) info.rec.tb = pred; dirty = true;
      }
    }
    if (dirty) this._bTex.needsUpdate = true;
  }

  // the bursts' lights: the strongest few, for the sky, the smoke, the rims and the water
  _updateLights() {
    const T = this._time, L = this._L, cand = [];
    let total = 0, bx = 0, bz = 0;
    for (let s = 0; s < L.slots; s++) {
      const f = this._slotInfo[s];
      if (!f) continue;
      const age = T - f.tb;
      if (age >= 0 && age < 1.4) {
        const I = f.light * (Math.exp(-age / 0.3) + 1.2 * Math.exp(-age / 0.05)) * (f.comet ? 0.5 : 1);
        cand.push({ x: f.x, y: f.y, I, r: f.comet ? 0.25 : 0.4, col: f.col });
        total += I;
      }
      const la = T - f.tL;
      if (la >= 0 && la < 0.15 && !f.comet) { const k = 1 - la / 0.15; if (k > bz) { bz = k; bx = f.lx; } }
    }
    cand.sort((a, b) => b.I - a.I);
    for (let i = 0; i < L.lights; i++) {
      const c = cand[i];
      if (c) { this._lp[i].set(c.x, c.y, c.I, c.r); this._lc[i].copy(c.col); }
      else this._lp[i].set(0, 0, 0, 0.3);
    }
    this._bayMat.uniforms.uBarge.value.set(bx, 0, bz, 0);
    this._lightTotal = total;
    return total;
  }

  update(deltaTime, profile) {
    const pv = profile.artworkPalette && profile.artworkPalette.vividColors && profile.artworkPalette.vividColors.length ? profile.artworkPalette.vividColors : FALLBACK;
    const pkey = pv.join(',');
    if (pkey !== this._paletteRef) {
      this._paletteRef = pkey;
      const v = pv;
      this._vivid = v.slice(0, 4);
      this._newPhrase();
    }
    const playing = profile.playbackState === undefined || profile.playbackState === 'PLAYING';
    const dt = playing ? Math.min(deltaTime, 0.1) : 0;
    this._clock.update(dt);
    if (dt === 0) return;                         // paused: frozen in the air
    this._time += dt;
    const a = this._adapt.update(dt, profile);
    const director = profile.director;
    const bpm = (profile.trackFeatures && profile.trackFeatures.bpm) || 0;
    // the beat (or, without one, a free clock at 120 BPM)
    let beat = false, bar = false, phrase = false;
    const bp = profile.beatPhase;
    const spbNew = bpm > 0 ? 60 / Math.max(60, Math.min(200, bpm)) : 0.5;
    this._secondsBeat = spbNew;
    if (bp != null) { if (this._phaseBeat != null && bp < this._phaseBeat - 0.5) beat = true; this._phaseBeat = bp; this._beatPhase = bp; }
    else { this._free += dt; if (this._free >= this._secondsBeat) { this._free -= this._secondsBeat; beat = true; } this._beatPhase = this._free / this._secondsBeat; }
    if (beat) this._beats++;
    const barPhase = profile.barPhase;
    if (barPhase != null) { if (this._phaseBar != null && barPhase < this._phaseBar - 0.5) bar = true; this._phaseBar = barPhase; this._barPhase = barPhase; }
    else { if (beat && this._beats % 4 === 0) bar = true; this._barPhase = ((this._beats % 4) + this._beatPhase) / 4; }
    const ph = profile.phrasePhase;
    if (ph != null) { if (this._phrasePhase != null && ph < this._phrasePhase - 0.5) phrase = true; this._phrasePhase = ph; }
    else if (bar && this._bars % 4 === 3) phrase = true;
    if (bar) this._bars++;
    if (phrase) { this._phrases++; this._newPhrase(); }

    // the energy cursor, the build, the calm
    this._intensity += ((a ? a.intensity : 0.6) - this._intensity) * (1 - Math.exp(-dt / 1.5));
    const next = director && director.nextEvent;
    let build = 0;
    if (next && next.type === 'drop' && next.msUntil < 8000) build = 1 - next.msUntil / 8000;
    if (director && director.mode === 'build') build = Math.max(build, 0.6);
    this._build += (build - this._build) * (1 - Math.exp(-dt / 0.8));
    this._calmMode = !!(director && director.mode === 'breakdown');
    // the DROP is known ahead: its salvo is planned for the beat nearest to it
    if (next && next.type === 'drop' && next.msUntil != null && next.msUntil < HORIZON * 1000 + 100 && profile.estimatedPosition != null) {
      const key = Math.round((profile.estimatedPosition * 1000 + next.msUntil) / 100);
      if (key !== this._lastDropKey) {
        this._lastDropKey = key;
        const spb = this._secondsBeat, Bc = this._beats + this._beatPhase;
        const Xd = Math.round(Bc + (next.msUntil / 1000) / spb);
        const td = this._time + (Xd - Bc) * spb;
        if (td - this._time > 0.35) { this._dropAt = td; this._lastDropT = td; if (Xd <= this._lastPlanned) this._planEighth(Xd, td, this._time, Bc); }
      }
    } else if (a && a.drop && this._dropAt == null && (this._lastDropT == null || Math.abs(this._time - this._lastDropT) > 4)) {
      // a drop we did not see coming: the salvo on the beat at least one second away
      const spb = this._secondsBeat, Bc = this._beats + this._beatPhase;
      const Xd = Math.ceil(Bc + 1.0 / spb);
      const td = this._time + (Xd - Bc) * spb;
      this._dropAt = td; this._lastDropT = this._time;
      if (Xd <= this._lastPlanned) this._planEighth(Xd, td, this._time, Bc);
    }

    this._resync();
    this._plan();

    // the drop's flash, on the burst itself (VdjComfort decides)
    if (this._pendingFlashAt != null && this._time >= this._pendingFlashAt) {
      this._pendingFlashAt = null;
      if (this._comfort.requestFlash(this._time)) { this._flash = 1; if (director) director.flash = VDJ_DROP_FLASH; }
    }
    this._flash *= Math.pow(0.5, dt / 0.08);

    const total = this._updateLights();
    // comfort (R10): the light of the bursts, after the current gain
    const est = (0.018 + 0.035 * total + 0.4 * this._flash) * this._comfort.gain;
    const gain = this._comfort.measure(est, dt);
    this._exposure = gain;
    // the wind changes with the scene
    if (this._clock.wrapped || this._wind == null) this._wind = (this._rnd() < 0.5 ? -1 : 1) * (0.008 + 0.012 * this._rnd());
    this._smokeMat.uniforms.uWind.value.set(this._wind, 0);
    this._post.update(vdjBeatChroma(this._beatPhase, this._secondsBeat));
  }

  _render(renderer) {
    renderer.getDrawingBufferSize(this._size);
    const W = Math.max(2, this._size.x), H = Math.max(2, this._size.y);
    const hw = Math.max(2, W >> 1), hh = Math.max(2, H >> 1);
    if (!this._rt || this._rt.width !== hw || this._rt.height !== hh) {
      if (this._rt) this._rt.dispose();
      this._rt = new THREE.WebGLRenderTarget(hw, hh, { depthBuffer: false, type: THREE.UnsignedByteType, minFilter: THREE.LinearFilter, magFilter: THREE.LinearFilter, generateMipmaps: false });
      this._bayMat.uniforms.uSmoke.value = this._rt.texture;
    }
    const T = this._time;
    const waterT = this._L.refl ? T : 0;            // tier 0: the ripples hold still
    const st = safeZoneUniforms.uStageCenter.value;
    const bu = this._bayMat.uniforms;
    bu.uPx.value = 2 / H; bu.uTime.value = T % 1000; bu.uWaterT.value = waterT % 1000; bu.uGain.value = this._exposure;
    bu.uStage.value.copy(st);
    for (const m of [this._sparkMat, this._reflMat]) {
      if (!m) continue;
      const u = m.uniforms;
      u.uTime.value = T; u.uGain.value = this._exposure; u.uPxScale.value = Math.max(0.6, H / 1080); u.uWaterT.value = waterT % 1000;
      u.uWaterPx.value = (MIRROR_Y + 1) * 0.5 * H;
    }
    this._smokeMat.uniforms.uTime.value = T;
    const prev = renderer.getRenderTarget(), auto = renderer.autoClear, cc = renderer.getClearColor(new THREE.Color()), ca = renderer.getClearAlpha();
    renderer.setRenderTarget(this._rt); renderer.setClearColor(0x000000, 0); renderer.autoClear = true;
    renderer.render(this._priv, this._privCam);
    renderer.setRenderTarget(prev); renderer.autoClear = auto; renderer.setClearColor(cc, ca);
  }

  get probe() {
    const T = this._time;
    const live = this._slotInfo.filter((f, s) => f && this._slotEnd[s] > T).length;
    return { t: +T.toFixed(3), beats: this._beats, beatPhase: +this._beatPhase.toFixed(3), spb: +this._secondsBeat.toFixed(4), scene: +this._clock.t.toFixed(2),
      intensity: +this._intensity.toFixed(2), build: +this._build.toFixed(2), live, planned: this._stats.planned, dropped: this._stats.dropped,
      gain: +this._comfort.gain.toFixed(3), lights: +(this._lightTotal || 0).toFixed(2), types: this._phraseTypes, dropAt: this._dropAt,
      bursts: this._stats.bursts.slice(-40).map((b) => [+b.tb.toFixed(4), b.X, b.type]) };
  }

  dispose() {
    if (this._display) { this._display.onBeforeRender = null; if (this._scene) this._scene.remove(this._display); this._display.geometry.dispose(); }
    if (this._scene) { if (this._sparks) this._scene.remove(this._sparks); if (this._refl) this._scene.remove(this._refl); }
    for (const x of [this._geo, this._geoR, this._smokeGeo, this._bayMat, this._sparkMat, this._reflMat, this._smokeMat, this._bTex, this._sTex, this._rt]) if (x) x.dispose();
    if (this._adapt && this._adapt.dispose) this._adapt.dispose();
    this._scene = null;
  }
}

function spbHalf(spb) { return spb * 0.26; }
